import {faImage, faRecycle, faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import isBase64 from "is-base64";
import moment from "moment";
import React, {Component} from "react";
import {withTranslation} from "react-i18next";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import ReactTable from "react-table";
import "react-table/react-table.css";
import {
    downloadImage,
    getDocumentNameList,
    searchKycUserProfile,
    updateActiveProfileStatus,
} from "../../api/IrsClient";
import ImageBrokenError from "../../resources/image/image_broken_error.png";
import Loading from "../../resources/image/loading.gif";
import blobToBase64 from "../../utils/blobToBase64";
import capitalizeString from "../../utils/capitalizeString.js";
import ActionButton from "../common/ActionButton";
import ModalError from "../common/ModalError";
import KycProfileSearchForm from "./KycProfileSearchForm";
import ModalNotFound from "../common/ModalNotFound";

const TEXT_CELL_STYLE = {
  alignContent: "center",
  fontSize: "12px",
};

const initState = {
  data: [],
  page: 0,
  pageSize: 20,
  pages: 1,
  pageNumList: [1],
  pageSizeOptions: [20, 50, 100],
  transactionIdForImageRendering: "",
  imageTypeForRendering: "",
  imagesForRendering: [],
  imageIndex: 0,
  isOpenLightBox: false,
  loading: false,
  showPagination: true,
  cacheRequestBody: null,
  disable: false,
  isOpenModal: false,
  modalMessage: "",
  sessionTimeout: false,
};

class KycProfileSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...initState,
      notFound: null,
    };
  }

  componentDidUpdate() {
    if (!this.state.loading && !this.state.showPagination) {
      this.setState({ showPagination: true });
    }
  }

  onClear = () => {
    this.setState({
      ...initState,
      showPagination: false,
    });
  };

  onPageChange = (page) => {
    this.setState({
      page: page,
    });

    this.fetchProfileDataOnChangePage(page, this.state.pageSize);
  };

  onPageSizeChange = (newPageSize, newPage) => {
    this.setState({
      page: newPage,
      pageSize: newPageSize,
    });

    this.fetchProfileDataOnChangePage(newPage, newPageSize);
  };

  convertDateToString = (dateStr) => {
    let returnedDate = "-";

    if (dateStr && dateStr !== "") {
      returnedDate = moment(dateStr).format("DD/MM/YYYY");
    }

    return returnedDate;
  };

  convertBooleanToNY = (booleanValue) => {
    return !booleanValue ? "N" : "Y";
  };

  selectValueInTheCell = (actualValue) => {
    return !actualValue ? "-" : actualValue;
  };

  convertDocumentStatusToUI = (actualValue) => {
    let status;

    switch (actualValue) {
      case "UPLOADED":
        status = "Uploaded";
        break;
      case "NOT_UPLOADED":
        status = "Not Uploaded";
        break;
      case "REMOVED":
        status = "Removed";
        break;
      default:
        status = "-";
    }

    return status;
  };

  callSearchApi = (requestBody) => {
    this.setState({
      cacheRequestBody: requestBody,
      loading: true,
      disable: true,
    });

    searchKycUserProfile(requestBody).then((response) => {
      const { error } = response;
      let data = [];
      let { page } = requestBody;
      let pages = this.state.pages;
      let pageSize = this.state.pageSize;
      let isOpenModal = false;
      let modalMessage = "";
      let sessionTimeout = false;

      if (error) {
        const errorCode = error["errorCode"];
        sessionTimeout = errorCode === "401" || errorCode === "403";
        isOpenModal = true;
        modalMessage = error["errorMessage"];
        this.setState({
          data: data,
          loading: false,
          disable: false,
          page: page - 1,
          pages: pages,
          showPagination: false,
          sessionTimeout: sessionTimeout,
          isOpenModal: isOpenModal,
          modalMessage: modalMessage,
        });
      } else {
        const { data: responseData } = response;
        const { totalResults } = response;
        if (responseData.length !== 0) {
          data = responseData;
          pages = Math.ceil(totalResults / pageSize);
          this.setState({
            data: data,
            loading: false,
            disable: false,
            page: page - 1,
            pages: pages,
            showPagination: true,
            notFound: null,
          });
        } else {
          this.setState({
            data: [],
            loading: false,
            disable: false,
            page: 0,
            pages: 0,
            showPagination: false,
            notFound: { title: "" },
          });
        }
      }
    });
  };

  callRemoveApi = (cachedData) => {
    if (Object.entries(cachedData).length !== 0) {
      this.setState({
        loading: true,
        disable: true,
      });
      const { transactionId, changedStatus, dataIndex } = cachedData;
      updateActiveProfileStatus(transactionId, changedStatus).then(
        (response) => {
          const { error } = response;
          let isOpenModal = false;
          let modalMessage = "";
          let sessionTimeout = false;

          if (error) {
            const errorCode = error["errorCode"];
            sessionTimeout = errorCode === "401" || errorCode === "403";
            isOpenModal = true;
            modalMessage = error["errorMessage"];
          } else {
            const targetUserProfile = this.state.data[dataIndex];
            targetUserProfile["isActive"] = changedStatus;
          }

          this.setState({
            loading: false,
            disable: false,
            sessionTimeout: sessionTimeout,
            isOpenModal: isOpenModal,
            modalMessage: modalMessage,
          });
        }
      );
    }
  };

  callViewApi = (cachedData) => {
    if (Object.entries(cachedData).length !== 0) {
      const { transactionId, senderId, senderIdType } = cachedData;
      const type = senderIdType.toUpperCase().trim().replace(" ", "_");

      this.setState({
        transactionIdForImageRendering: transactionId,
        imageTypeForRendering: type,
      });

      getDocumentNameList(transactionId, senderId, type).then((response) => {
        const { error } = response;
        let isOpenModal = false;
        let modalMessage = "";
        let sessionTimeout = false;

        if (error) {
          const errorCode = error["errorCode"];
          sessionTimeout = errorCode === "401" || errorCode === "403";
          isOpenModal = true;
          modalMessage = error["errorMessage"];

          this.setState({
            sessionTimeout: sessionTimeout,
            isOpenModal: isOpenModal,
            modalMessage: modalMessage,
          });
        } else if (
          this.state.transactionIdForImageRendering === transactionId &&
          this.state.imageTypeForRendering === type
        ) {
          const { imageList } = response;
          const loadingScreens = [];

          // set loading screens
          for (let i = 0; i < imageList.length; i++) {
            loadingScreens[i] = Loading;
          }

          this.setState({
            imagesForRendering: loadingScreens,
            isOpenLightBox: true,
          });

          this.callDownloadImageApi(
            imageList,
            transactionId,
            senderId,
            type
          ).finally();
        }
      });
    }
  };

  callDownloadImageApi = async (imageList, transactionId, senderId, type) => {
    // download all images based on their file name and render them on light boxes.
    for (let i = 0; i < imageList.length; i++) {
      const { name } = imageList[i];
      const imagesResponse = await downloadImage(name, senderId, type, i);
      const { image, index } = imagesResponse;

      if (
        this.state.transactionIdForImageRendering === transactionId &&
        this.state.imageTypeForRendering === type
      ) {
        const copyImageList = this.state.imagesForRendering.slice();
        try {
          // // Show image if it exists.
          if (isBase64(await image.text(), { allowMime: true })) {
            copyImageList[i] = await image.text();
          } else {
            copyImageList[i] = await blobToBase64(image);
          }
          const containsJpeg = copyImageList[i].includes("/jpeg");
          if (!containsJpeg) {
            copyImageList[i] = ImageBrokenError;
          }
        } catch (e) {
          // If not, show image not found
          copyImageList[index] = ImageBrokenError;
        }

        this.setState({
          imagesForRendering: copyImageList,
        });
      }
    }
  };

  fetchProfileDataOnChangePage = (page, pageSize) => {
    const requestBody = this.state.cacheRequestBody;

    if (requestBody) {
      requestBody["page"] = page + 1;
      requestBody["pageSize"] = pageSize;

      this.callSearchApi(requestBody);
    } else {
      this.setState({
        page: page,
        pageSize: pageSize,
      });
    }
  };

  fetchProfileData = (params) => {
    const {
      branchIds,
      transactionStartDate,
      transactionEndDate,
      identity,
      workPermitId,
      customerName,
      selectedDocumentTypes,
      selectedKycLevels
    } = params;

    const startDateStr = moment(transactionStartDate).format("YYYY-MM-DD");
    const endDateStr = moment(transactionEndDate).format("YYYY-MM-DD");

    const requestBody = {};

    requestBody["transactionDateFrom"] = startDateStr + "T00:00:00";
    requestBody["transactionDateTo"] = endDateStr + "T23:59:59";

    if (branchIds && branchIds !== "") {
      requestBody["branchIds"] = branchIds.split(",");
    }

    if (Array.isArray(selectedDocumentTypes) && selectedDocumentTypes.length) {
      requestBody["documentStatus"] = selectedDocumentTypes.map(
        (documentType) => documentType["value"]
      );
    }

    if (identity && identity !== "") {
      requestBody["identifierNo"] = identity;
    }

    if (workPermitId && workPermitId !== "") {
      requestBody["workPermitNo"] = workPermitId;
    }

    if (customerName && customerName !== "") {
      requestBody["name"] = customerName;
    }

    if (Array.isArray(selectedKycLevels) && selectedKycLevels.length) {
      requestBody["kycLevel"] = selectedKycLevels.map(
          (kycLevel) => kycLevel["value"]
      );
    }

    requestBody["page"] = 1;
    requestBody["pageSize"] = this.state.pageSize;

    this.callSearchApi(requestBody);
  };

  populateIdViewBtn = (cellInfo) => {
    const { original } = cellInfo;
    const {
      transactionId,
      senderId,
      isIdcardOrPpUpdateRequired,
      senderIdType,
    } = original;

    const cacheData = {
      transactionId: transactionId,
      senderId: senderId,
      senderIdType: senderIdType,
    };

    return isIdcardOrPpUpdateRequired || !senderIdType ? (
      "Not uploaded"
    ) : (
      <ActionButton
        onClick={this.callViewApi}
        className="btn btn-info btn-xs"
        cachedData={cacheData}
        fontIcon={<FontAwesomeIcon icon={faImage} />}
        label="View"
      />
    );
  };

  populateWpViewBtn = (cellInfo) => {
    const { original } = cellInfo;
    const {
      transactionId,
      senderId,
      isWpUpdateRequired,
      workPermitType,
    } = original;

    const cacheData = {
      transactionId: transactionId,
      senderId: senderId,
      senderIdType: workPermitType,
    };

    return workPermitType ? (
      isWpUpdateRequired ? (
        "Not uploaded"
      ) : (
        <ActionButton
          onClick={this.callViewApi}
          cachedData={cacheData}
          className="btn btn-info btn-xs"
          fontIcon={<FontAwesomeIcon icon={faImage} />}
          label="View"
        />
      )
    ) : (
      <span style={{ color: "LightGray" }}>No work permit</span>
    );
  };

  populateRemoveBtn = (cellInfo) => {
    const {
      isWpUpdateRequired,
      isIdcardOrPpUpdateRequired,
      isActive,
      transactionId,
      workPermitType,
    } = cellInfo.original;
    const changedStatus = !isActive;
    const cachedData = {
      dataIndex: cellInfo.index,
      transactionId: transactionId,
      changedStatus: changedStatus,
    };
    return isActive &&
      ((isWpUpdateRequired && isIdcardOrPpUpdateRequired) ||
        (!workPermitType && isIdcardOrPpUpdateRequired)) ? (
      <ActionButton
        data-testid="btn-kyc-remove-file"
        onClick={this.callRemoveApi}
        cachedData={cachedData}
        disabled={false}
        fontIcon={<FontAwesomeIcon icon={faTrash} />}
        className="btn btn-danger btn-xs"
        label="Remove"
      />
    ) : !isActive ? (
      <ActionButton
        onClick={this.callRemoveApi}
        cachedData={cachedData}
        disabled={false}
        fontIcon={<FontAwesomeIcon icon={faRecycle} />}
        className="btn btn-light btn-xs"
        label="Unremove"
      />
    ) : (
      <ActionButton
        onClick={this.callRemoveApi}
        cachedData={cachedData}
        disabled={true}
        fontIcon={<FontAwesomeIcon icon={faTrash} />}
        className="btn btn-secondary btn-xs"
        label="Remove"
      />
    );
  };

  prepareColumns = () => {
    const { userGroup } = this.props;
    let columns = [
      {
        Header: "#",
        accessor: "index",
        width: 50,
        style: TEXT_CELL_STYLE,
        Cell: (props) => {
          const { index } = props;
          return index + 1 + this.state.page * this.state.pageSize;
        },
      },
      {
        Header: "Branch ID",
        accessor: "branchId",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "Branch Name",
        accessor: "branchName",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "Name",
        accessor: "senderFirstname",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "Surname",
        accessor: "senderLastname",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "KYC Level",
        accessor: "kycLevel",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "Point of Transaction",
        accessor: "pointOfTransaction",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => capitalizeString(cellInfo.value),
      },
      {
        Header: "Document Status",
        accessor: "documentStatus",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.convertDocumentStatusToUI(cellInfo.value),
      },
      {
        Header: "Transaction Date",
        accessor: "createdDate",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.convertDateToString(cellInfo.value),
      },
      {
        Header: "ID Card/Passport No.",
        accessor: "senderId",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "Nationality",
        accessor: "senderNationality",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "ID/PP Uploaded Date",
        accessor: "idcardOrPpUpdateDate",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.convertDateToString(cellInfo.value),
      },
      {
        Header: "ID/PP Uploaded By",
        accessor: "idcardOrPpUpdateBy",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "Required Update ID/PP?",
        accessor: "isIdcardOrPpUpdateRequired",
        width: 200,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.convertBooleanToNY(cellInfo.value),
      },
      {
        Header: "ID/PP Accumulated Day",
        accessor: "idcardOrPpAccumulatedDay",
        width: 200,

        style: TEXT_CELL_STYLE,
      },
      {
        Header: "Work Permit No",
        accessor: "workPermitNumber",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => {
          const { original } = cellInfo;
          const { workPermitType, workPermitNumber } = original;

          return workPermitType ? (
            workPermitType.toUpperCase() === "RECEIPT" ? (
              "Receipt"
            ) : workPermitNumber ? (
              workPermitNumber
            ) : (
              <span style={{ color: "LightGray" }}>No work permit</span>
            )
          ) : (
            <span style={{ color: "LightGray" }}>No work permit</span>
          );
        },
      },
      {
        Header: "WP Uploaded Date",
        accessor: "wpUploadDate",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.convertDateToString(cellInfo.value),
      },
      {
        Header: "WP Uploaded By",
        accessor: "wpUploadBy",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.selectValueInTheCell(cellInfo.value),
      },
      {
        Header: "Required Update WP?",
        accessor: "isWpUpdateRequired",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.convertBooleanToNY(cellInfo.value),
      },
      {
        Header: "WP Accumulated Day",
        accessor: "wpAccumulatedDay",
        width: 150,

        style: TEXT_CELL_STYLE,
      },
      {
        Header: "ID Card/PP Photo",
        accessor: "idPhoto",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.populateIdViewBtn(cellInfo),
      },
      {
        Header: "WP Photo",
        accessor: "workPermitPhoto",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.populateWpViewBtn(cellInfo),
      },
    ];
    if (userGroup.includes("/services/irs/irs-administrator")) {
      columns.push({
        Header: "Remove Record",
        accessor: "removeRecord",
        width: 150,

        style: TEXT_CELL_STYLE,
        Cell: (cellInfo) => this.populateRemoveBtn(cellInfo),
      });
    }
    return columns;
  };

  requestModalCloseHandler = () => {
    this.setState({
      isOpenModal: false,
    });

    if (this.state.sessionTimeout) {
      const { logout } = this.props;
      logout();
    }
  };

  render() {
    const { t } = this.props;
    const columns = this.prepareColumns();
    return (
      <div>
        <ModalNotFound
            message={"Data not found."}
            isOpen={!!this.state.notFound}
            description={this.state.notFound?.description ?? ""}
            onRequestClose={() => this.setState({ notFound: null })}
        />
        <div className="card">
          <div className="card-header">
            <h5>{t("kyc-profile.title")}</h5>
          </div>
          <KycProfileSearchForm
            onSubmit={this.fetchProfileData}
            onClear={this.onClear}
            disable={this.state.disable}
          />
        </div>
        <div className="card mt-2">
          <div className="card-body">
            <ReactTable
              className="-highlight report"
              columns={columns}
              data={this.state.data}
              loading={this.state.loading}
              manual
              onPageChange={this.onPageChange}
              onPageSizeChange={this.onPageSizeChange}
              page={this.state.page}
              pages={this.state.pages}
              pageSize={this.state.pageSize}
              pageSizeOptions={this.state.pageSizeOptions}
              showPagination={this.state.showPagination}
              showPaginationTop
            />
          </div>
        </div>
        <ModalError
          isOpen={this.state.isOpenModal}
          onRequestClose={this.requestModalCloseHandler}
          message={this.state.modalMessage}
        />
        {this.state.isOpenLightBox && (
          <Lightbox
            mainSrc={this.state.imagesForRendering[this.state.imageIndex]}
            nextSrc={this.state.imagesForRendering[this.state.imageIndex + 1]}
            prevSrc={this.state.imagesForRendering[this.state.imageIndex - 1]}
            onCloseRequest={() =>
              this.setState({
                isOpenLightBox: false,
                imageIndex: 0,
                imagesForRendering: [],
              })
            }
            onMovePrevRequest={() =>
              this.setState({
                imageIndex:
                  (this.state.imageIndex +
                    this.state.imagesForRendering.length -
                    1) %
                  this.state.imagesForRendering.length,
              })
            }
            clickOutsideToClose={false}
            onMoveNextRequest={() =>
              this.setState({
                imageIndex:
                  (this.state.imageIndex + 1) %
                  this.state.imagesForRendering.length,
              })
            }
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(KycProfileSearch);
